import { ChevronDownIcon, Loader2, PlusIcon, Settings } from 'lucide-react'
import * as React from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'

export interface Environment {
  id: string
  name: string
  type: 'development' | 'production' | 'staging'
}

interface EnvironmentSelectorProps {
  currentEnvironment: Environment | null
  environments: Environment[]
  isLoading?: boolean
  error?: Error | null
  onRetry?: () => void
  onEnvironmentChange: (environment: Environment) => void
  onAddEnvironment: (name: string, type: Environment['type']) => void
  onSettingsClick?: () => void
  className?: string
}

export function EnvironmentSelector({
  currentEnvironment,
  environments,
  isLoading = false,
  error,
  onRetry,
  onEnvironmentChange,
  onAddEnvironment,
  onSettingsClick,
  className,
}: EnvironmentSelectorProps) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [newEnvironmentName, setNewEnvironmentName] = React.useState('')
  const [newEnvironmentType, setNewEnvironmentType] =
    React.useState<Environment['type']>('development')

  const handleAddEnvironment = () => {
    if (newEnvironmentName.trim()) {
      onAddEnvironment(newEnvironmentName.trim(), newEnvironmentType)
      setNewEnvironmentName('')
      setNewEnvironmentType('development')
      setIsDialogOpen(false)
    }
  }

  // Use the first environment if no current environment is set, or show loading state
  const displayEnvironment = currentEnvironment || environments[0]
  const showLoadingState = isLoading
  const showErrorState = !isLoading && !!error

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Badge
            variant='secondary'
            className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-secondary/80 transition-colors ${showLoadingState ? 'pointer-events-none opacity-50' : ''}`}
          >
            {showLoadingState ? (
              <>
                <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />
                <span className='font-medium'>Loading environments...</span>
              </>
            ) : showErrorState ? (
              <>
                <span className='font-medium text-destructive'>
                  Failed to load
                </span>
                <ChevronDownIcon className='h-3 w-3 text-muted-foreground' />
              </>
            ) : (
              <>
                <span className='font-medium'>
                  {displayEnvironment?.name || 'No Environment'}
                </span>
                <ChevronDownIcon className='h-3 w-3 text-muted-foreground' />
              </>
            )}
          </Badge>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start' className='w-64'>
          <div className='px-2 py-1.5'>
            <p className='text-sm text-muted-foreground'>
              Select an environment
            </p>
          </div>
          <DropdownMenuSeparator />
          {showLoadingState ? (
            <div className='px-2 py-2 flex items-center gap-2'>
              <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
              <span className='text-sm text-muted-foreground'>
                Loading environments...
              </span>
            </div>
          ) : showErrorState ? (
            <div className='px-2 py-2'>
              <span className='text-sm text-destructive'>
                Failed to load environments
              </span>
              {onRetry && (
                <DropdownMenuItem onClick={() => onRetry()} className='mt-1'>
                  Retry
                </DropdownMenuItem>
              )}
            </div>
          ) : environments.length === 0 ? (
            <div className='px-2 py-2'>
              <span className='text-sm text-muted-foreground'>
                No environments found
              </span>
            </div>
          ) : (
            environments.map(env => (
              <DropdownMenuItem
                key={env.id}
                onClick={() => onEnvironmentChange(env)}
                className='flex items-center gap-2 px-2 py-2'
              >
                <div className='flex flex-col'>
                  <span className='font-medium'>{env.name}</span>
                  <span className='text-xs text-muted-foreground capitalize'>
                    {env.type}
                  </span>
                </div>
                {env.id === displayEnvironment?.id && (
                  <div className='ml-auto w-1 h-1 bg-current rounded-full' />
                )}
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <DropdownMenuItem
                onSelect={e => e.preventDefault()}
                className='flex items-center gap-2 px-2 py-2'
              >
                <PlusIcon className='h-4 w-4' />
                New environment
              </DropdownMenuItem>
            </DialogTrigger>
            <DialogContent className='sm:max-w-md'>
              <DialogHeader>
                <DialogTitle>Create New Environment</DialogTitle>
                <DialogDescription>
                  Create a new environment for your application
                </DialogDescription>
              </DialogHeader>
              <div className='grid gap-4 py-4'>
                <div className='grid gap-2'>
                  <Label htmlFor='environment-name'>Environment Name</Label>
                  <Input
                    id='environment-name'
                    placeholder='Enter environment name'
                    value={newEnvironmentName}
                    onChange={e => setNewEnvironmentName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleAddEnvironment()
                      }
                    }}
                  />
                </div>
                <div className='grid gap-2'>
                  <Label htmlFor='environment-type'>Environment Type</Label>
                  <Select
                    value={newEnvironmentType}
                    onValueChange={(value: Environment['type']) =>
                      setNewEnvironmentType(value)
                    }
                  >
                    <SelectTrigger id='environment-type'>
                      <SelectValue placeholder='Select environment type' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='development'>Development</SelectItem>
                      <SelectItem value='production'>Production</SelectItem>
                      <SelectItem value='staging'>Staging</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className='flex gap-2'>
                <Button
                  variant='outline'
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleAddEnvironment}>
                  Create Environment
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </DropdownMenuContent>
      </DropdownMenu>
      {onSettingsClick && (
        <Button
          variant='ghost'
          size='sm'
          onClick={onSettingsClick}
          aria-label='Settings'
        >
          <Settings className='h-4 w-4' />
        </Button>
      )}
    </div>
  )
}
